import React, { useState } from 'react'
import { AdCreative } from '@marketing-milk/interfaces'
import { CreativePreview, getIFrameSource } from 'components/ad-preview'
import { Drawer, Tooltip } from '@material-ui/core'
import { sentenceCase } from 'change-case'
import { CreativePill } from './CreativePill'
import { CreativeVerdictBadge, hasClientApproval } from './CreativeVerdictBadge'
import { CreativeMedia } from './CreativeMedia'
import { CreativeTools } from '../components/creative-tools/CreativeTools'
import { CallCreativeIcon } from './CallCreativeIcon'
import { LeadCardIcon } from './LeadCardIcon'
import { CardIconButton } from './CardIconButton'
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined'
import OpenInNew from '@mui/icons-material/OpenInNew'
import { useFetchCreativePreview } from '../../../../hooks/creatives/fetch-creative-preview'

type StringKeys<T> = { [P in keyof T]: NonNullable<T[P]> extends String ? P : never }[keyof T]

type Props = {
  creative: AdCreative
  selected?: boolean
  /** Ribbon label for a selected card. Separate from `selected` because the label is
   * the campaign picker's ad-set letter, which means nothing on screens that pick ads
   * for some other reason — they mark the card without stamping a letter on it. */
  selectedText?: string
  /** Dims the card and blocks the click outright. */
  disabled?: boolean
  /**
   * Why this ad is withheld from being picked, stamped on it. Separate from
   * `disabled` because a withheld ad that is already attached still has to be
   * clickable — otherwise the ads that arrive pre-attached have no way off the
   * campaign on the screen that reports them as the problem.
   */
  withheldReason?: string
  pillDisplayed?: keyof Pick<AdCreative, 'format' | 'status' | 'reviewStatus'>
  propertiesDisplayed?: StringKeys<AdCreative>[]
  tools?: boolean
  objectFit?: 'cover' | 'contain'
  onSelect: (adCreative: AdCreative) => void
}
export const CreativeCard = ({
  creative,
  propertiesDisplayed = ['name'],
  tools = true,
  objectFit,
  ...props
}: Props) => {
  const [viewPreview, setViewPreview] = useState(false)

  const { adCreativePreview, retrievePreview, loadingPreview, refreshing } =
    useFetchCreativePreview(creative.id, undefined, { suppressErrorToast: true })

  const dimmed = props.disabled || !!props.withheldReason
  const selected = props.selected ?? Boolean(props.selectedText)
  const statusLabel = props.withheldReason || (props.disabled ? 'Published' : undefined)
  const pillProperty = props.pillDisplayed ?? 'format'

  // The card takes its width and spacing from the grid that holds it: a card that sizes
  // itself leaves every consumer with two spacing systems and columns that govern nothing.
  const className = `h-full min-w-0 w-full bg-white transition-shadow z-0 overflow-hidden duration-200 rounded-xl relative flex flex-col border ${
    dimmed ? 'opacity-50' : 'hover:shadow-md'
  } ${props.disabled ? 'cursor-not-allowed' : 'cursor-pointer'} ${
    selected
      ? 'border-mm-primary-600 ring-1 ring-mm-primary-600'
      : 'border-mm-neutral-1-200 hover:border-mm-neutral-1-300'
  }`

  return (
    <section className={className} data-testid={creative.name}>
      <div className="absolute top-2 right-2 z-10 flex items-center gap-1">
        <CallCreativeIcon creative={creative} />
        <LeadCardIcon creative={creative} />
      </div>

      <div
        className="flex flex-1 flex-col focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-mm-primary-600"
        role="button"
        tabIndex={props.disabled ? -1 : 0}
        aria-label={creative.name || 'Unnamed creative'}
        aria-disabled={Boolean(props.disabled)}
        aria-pressed={props.selected !== undefined || props.selectedText ? selected : undefined}
        onClick={() => !props.disabled && props.onSelect(creative)}
        onKeyDown={event => {
          // Menu controls handle their own keys, including controls rendered in portals.
          if (event.target !== event.currentTarget) return
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault()
            if (!props.disabled) props.onSelect(creative)
          }
        }}
      >
        <div className="relative">
          <CreativeMedia
            creative={creative}
            objectFit={objectFit}
            className="h-64 w-full bg-mm-neutral-1-100"
          />

          {pillProperty !== 'format' && (
            <div className="absolute top-2 left-2 z-10">
              <CreativePill creative={creative} property={pillProperty} />
            </div>
          )}
        </div>

        <div className="flex flex-1 flex-col gap-1 px-4 pt-3 pb-1">
          {(statusLabel || selected) && (
            <div className="flex flex-wrap items-center gap-2 pb-1">
              {statusLabel && (
                <span
                  className={`rounded-md px-2 py-1 text-xs font-semibold ${
                    props.withheldReason
                      ? 'bg-red-100 text-red-800'
                      : 'bg-mm-neutral-1-100 text-mm-neutral-1-700'
                  }`}
                >
                  {statusLabel}
                </span>
              )}
              {selected && (
                <span className="inline-flex items-center gap-1 rounded-md bg-mm-primary-50 px-2 py-1 text-xs font-semibold text-mm-primary-700">
                  <i className="fa fa-check" aria-hidden="true" />
                  {props.selectedText || 'Selected'}
                </span>
              )}
            </div>
          )}
          {hasClientApproval(creative) && (
            <div>
              <CreativeVerdictBadge />
            </div>
          )}

          {propertiesDisplayed.map(
            property =>
              property && (
                <dl className="m-0 min-w-0" key={property as string}>
                  <dt
                    className={
                      property === 'name'
                        ? 'sr-only'
                        : 'text-xs font-medium uppercase tracking-wide text-mm-neutral-1-500'
                    }
                  >
                    {sentenceCase(property)}
                  </dt>
                  <dd
                    title={creative[property] || undefined}
                    className={`m-0 ${
                      property === 'name'
                        ? 'text-sm font-semibold leading-5 break-words'
                        : 'truncate text-sm'
                    } ${creative[property] ? 'text-mm-neutral-1-900' : 'text-red-800'}`}
                    style={
                      property === 'name'
                        ? {
                            display: '-webkit-box',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            overflow: 'hidden',
                          }
                        : undefined
                    }
                  >
                    {creative[property] || `${sentenceCase(property)} (incomplete)`}
                  </dd>
                </dl>
              )
          )}
        </div>
      </div>

      <div className="mt-auto flex items-center gap-2 px-4 pb-2">
        {pillProperty === 'format' && creative.format && (
          <span className="truncate text-xs text-mm-neutral-1-500">
            {sentenceCase(creative.format)}
          </span>
        )}
        <div className="-mr-1 ml-auto flex items-center">
          <CardIconButton
            label="Preview ad"
            icon={<VisibilityOutlined fontSize="small" />}
            onClick={() => {
              setViewPreview(true)
              retrievePreview()
            }}
          />
          {creative.destinationURL && (
            <CardIconButton
              label="Open link"
              icon={<OpenInNew fontSize="small" />}
              href={creative.destinationURL}
            />
          )}
          {tools && <CreativeTools creative={creative} />}
        </div>
      </div>

      <Drawer open={viewPreview} onClose={() => setViewPreview(false)}>
        <div className="flex flex-col p-4 h-screen overflow-hidden w-full space-y-4 ">
          <div className="flex justify-between align-middle items-center">
            <p className="text-center font-extrabold text-xl">Ad Preview</p>
            <button
              type="button"
              aria-label="Close ad preview"
              className="flex h-10 w-10 items-center justify-center rounded-lg text-mm-neutral-1-500 hover:bg-mm-neutral-1-100 focus-visible:ring-2 focus-visible:ring-mm-primary-600"
              onClick={() => setViewPreview(false)}
            >
              <i className="fa fa-times" aria-hidden="true" />
            </button>
          </div>
          {/* The preview can be taller than the drawer; scroll it (rather than
              clip-centering) so the unavailable banner + authorizer link below the
              card stay reachable. Thin scrollbar matches the nav/present pattern. */}
          <style>{`
            .ad-preview-scroll::-webkit-scrollbar { width: 6px; }
            .ad-preview-scroll::-webkit-scrollbar-track { background: transparent; }
            .ad-preview-scroll::-webkit-scrollbar-thumb { background: #dadde1; border-radius: 3px; }
            .ad-preview-scroll::-webkit-scrollbar-thumb:hover { background: #c4cdd6; }
          `}</style>
          <div
            className="flex-1 overflow-y-auto flex flex-col items-center ad-preview-scroll"
            style={{ scrollbarWidth: 'thin', scrollbarColor: '#dadde1 transparent' }}
          >
            <CreativePreview
              creative={creative}
              iframeSource={getIFrameSource(adCreativePreview ?? '')}
              loading={loadingPreview}
              refreshing={refreshing}
              onRetry={() => retrievePreview()}
            />
          </div>
        </div>
      </Drawer>
    </section>
  )
}
